import { computed, inject, nextTick, onBeforeMount, onMounted, ref, watch } from 'vue';
import { v4 as uuid } from 'uuid';
import { NodeType } from '@amplify/amp-module';

import { onlyDigitsRegEx } from '@/constants/regularExpressions.js';
import { GetNodeById, GetSelectedNodeId, GetSelectedSubNode } from '@/constants/getters';
import { SetSelectedNodeId, SetSelectedSubNode } from '@/constants/mutations';
import { ClearAddingNode, UpdateNodeData } from '@/constants/actions';
import { defaultNodesData, SubNodesDefaultData, SubNodeType } from '@/constants/builder';
import { GROUP_NODE_PORT_GROUPS } from '@/constants/amp';

import { debounce } from '@/utils/debounce';
import { objectToSortedArray } from '@/utils/amp-utils';
import useLimitations from '@/composables/useLimitations';
import { useStoreInNodes } from '@/composables/useStoreInNodes';

export const useNodeUtils = (props) => {
  const { store } = useStoreInNodes();
  const { canEditAmp } = useLimitations(store.value);

  const getGraph = inject('getGraph', () => {});
  const getNode = inject('getNode', () => {});

  const ampData = ref({});
  const uiData = ref({});

  const debouncedCallback = ref(null);
  const nodeSelf = ref(null);
  const nodeRef = ref(null);

  const isNodeWithBranches = computed(() => [NodeType.buttonGroup, NodeType.question].includes(props.nodeType));
  const nodeId = computed(() => nodeSelf.value?.id || '');
  const selectedNodeId = computed(() => store.value.getters[`builderStore/${GetSelectedNodeId}`]);
  const selectedSubNode = computed(() => store.value.getters[`builderStore/${GetSelectedSubNode}`]);

  onBeforeMount(() => {
    // Updating the $vuetify key for node components to get the reference to the Vuetify context of the App
    // this.$vuetify = context.?.vuetify?.framework || this.$vuetify;
    // todo: change this - as a fix, the same code was added in the node component created lifecycle
  });

  onMounted(async () => {
    nodeSelf.value = getNode();
    getUIData(nodeSelf.value.getData());

    nodeSelf.value.on('change:data', ({ current }) => {
      getUIData(current);
    });

    const node = store.value.getters[`builderStore/${GetNodeById}`]({
      id: nodeId.value,
      conversationId: uiData.value.conversationId,
    });

    getNodeData(node);

    if (isNodeWithBranches.value) {
      nodeSelf.value.prop('ports/groups', GROUP_NODE_PORT_GROUPS);
      calculatePortsFromSubNodes();
    }

    await nextTick();

    if (node?.type && [NodeType.carousel, NodeType.player, NodeType.plugin, NodeType.open].includes(node.type)) {
      // these nodes require a custom rendering because of the text areas and/or the images
      // carousel is handled in CarouselNode.vue
      // player is handled in PlayerNode.vue
      // plugin is handled in PluginNode.vue
      // open is handled in OpenNode.vue
      return;
    }

    emitNodeRenderDoneEvent();
  });

  const commitToStore = (data) => {
    // Data must be a number of keys contained at the root level of the Node Data
    if (!data.id) {
      data.id = nodeId.value;
    }

    store.value.dispatch(`builderStore/${UpdateNodeData}`, { data });
  };

  const getNodeData = (data) => {
    ampData.value = { ignoreWatch: true, ...ampData.value, ...data };
  };

  const getUIData = (data) => {
    if (!uiData.value) {
      uiData.value = {};
    }

    const mergedData = {
      ...defaultNodesData.UI.node.data,
      ...defaultNodesData.UI[props?.nodeType],
      ...data,
    };
    const keys = Object.keys(mergedData);

    for (const key of keys) {
      uiData.value[key] = mergedData[key];
    }

    // trigger reactivity
    uiData.value = { ...uiData.value };
  };

  const updateDelay = (delay) => {
    ampData.value = {
      ...ampData.value,
      delay: typeof delay === 'string' ? parseInt(delay.replace(onlyDigitsRegEx, '')) : delay,
    };
  };

  /**
   * Sets the zIndex of the cell higher than all the other nodes
   * so that the storage select component would not get overlaped.
   */
  const bringNodeToFront = () => {
    getGraph()?.getCellById(nodeSelf.value.id)?.toFront();
  };

  /**
   * Universal methods for adding and deleting node storage entries.
   * Will be used in message and button nodes.
   */
  const addStorageOption = (option) => {
    ampData.value = { ...ampData.value, ...option };
  };

  const deleteStorage = () => {
    ampData.value = { ...ampData.value, storage: null, storageType: undefined };
  };

  const emitNodeRenderDoneEvent = () => {
    const nodeEl = nodeRef.value?.$el;
    const size = nodeEl ? { width: nodeEl.offsetWidth, height: nodeEl.offsetHeight } : undefined;
    const event = new CustomEvent('amplify:node:render:done', { bubbles: true, detail: { id: nodeId.value, size } });

    getGraph().container.dispatchEvent(event);
  };

  // Node branch and sub nodes utils

  const selectedSubNodeIndex = computed(() => (nodeId.value === selectedNodeId.value ? selectedSubNode.value : -1));
  const subNodesArray = computed(() => objectToSortedArray(ampData.value.subNodes));

  const calculatePortsFromSubNodes = () => {
    // Creating one port for each sub node
    const ports = subNodesArray.value.map((subNode) => ({
      id: `${nodeId.value}_port_${subNode.id}`,
      group: 'out',
    }));

    nodeSelf.value.prop('ports/items', ports);
  };

  const addSubNode = () => {
    const subNodesCount = subNodesArray.value.length;

    const subNodeType = SubNodeType[props.nodeType];
    const newNode = {
      ...(SubNodesDefaultData[subNodeType] || {}),
      id: uuid(),
      sortIndex: subNodesCount,
    };

    store.value.dispatch(`builderStore/${ClearAddingNode}`);

    commitToStore({
      ...ampData.value,
      subNodes: { ...ampData.value.subNodes, [newNode.id]: newNode },
    });

    onSelectSubNode(subNodesCount);
  };

  const onSelectSubNode = (index = -1) => {
    if (!canEditAmp.value) {
      return;
    }

    if (index !== -1) {
      store.value.commit(`builderStore/${SetSelectedNodeId}`, nodeId.value);
    }

    store.value.commit(`builderStore/${SetSelectedSubNode}`, index);
  };

  watch(
    ampData,
    (newObj) => {
      if (newObj.ignoreWatch) {
        delete newObj.ignoreWatch;
      } else {
        if (isNodeWithBranches.value) {
          calculatePortsFromSubNodes();
        }

        if (debouncedCallback.value) {
          debouncedCallback.value.cancel();
        }

        debouncedCallback.value = debounce(() => {
          commitToStore({ id: nodeId.value, ...newObj });
        }).execute();
      }
    },
    { deep: true }
  );

  return {
    ampData,
    canEditAmp,
    nodeId,
    nodeRef,
    nodeSelf,
    selectedSubNodeIndex,
    subNodesArray,
    uiData,
    store,
    addStorageOption,
    addSubNode,
    bringNodeToFront,
    commitToStore,
    deleteStorage,
    emitNodeRenderDoneEvent,
    onSelectSubNode,
    updateDelay,
  };
};
